// frontend/src/components/OnboardingModal/OnboardingModal.jsx

import React, { useState, useEffect, useContext, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { 
    Button, Typography, Box, FormControl, InputLabel, 
    Select, MenuItem, Checkbox, CircularProgress, Tooltip, Alert, IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';
import { useTranslation } from 'react-i18next';
import './OnboardingModal.css';

import NewspaperIcon from '@mui/icons-material/Newspaper';
import CampaignIcon from '@mui/icons-material/Campaign';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';

const isFeatureEnabled = process.env.REACT_APP_ENABLE_ONBOARDING === 'true';

// --- ICON MAPPING ---
const industryIcons = {
    news_and_media: <NewspaperIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />,
    advertising_and_marketing: <CampaignIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />,
    cybersecurity_and_fraud: <SecurityIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />,
    legal_and_insurance: <GavelIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />,
    social_and_dating: <PeopleIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />,
    education_and_academia: <SchoolIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />,
    healthcare_and_biotech: <HealthAndSafetyIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />,
    financial_services: <AccountBalanceIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />,
    general_enterprise: <BusinessIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />
};

function OnboardingModal() {
    const { t } = useTranslation();
    const { user, fetchWithAuth, updateUserSetting } = useContext(AuthContext);
    
    // --- Use global state to control visibility ---
    const { isOnboardingModalOpen, setIsOnboardingModalOpen, setIsPageBlurred } = useContext(UIContext); 
    
    const [configData, setConfigData] = useState(null);
    const [selectedIndustry, setSelectedIndustry] = useState(user?.analysis_preferences?.industry || '');
    const [selectedTasks, setSelectedTasks] = useState(user?.analysis_preferences?.displayed_tasks || []);
    const [isSaving, setIsSaving] = useState(false);
    const [devDismissed, setDevDismissed] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [hasAutoOpened, setHasAutoOpened] = useState(false); // Track so it only auto-opens once per session

    // 1. Check if user needs onboarding (Auto-open logic)
    useEffect(() => {
        if (!isFeatureEnabled) return;
        if (user && !user.email?.endsWith('@guest.local')) {
            const prefs = user.analysis_preferences || {};
            const isDev = process.env.NODE_ENV === 'development';
            
            // Only auto-open if they haven't set an industry, OR in dev mode if not dismissed
            if (!prefs.industry || (isDev && !devDismissed)) {
                if (!hasAutoOpened) {
                    setIsOnboardingModalOpen(true);
                    setHasAutoOpened(true);
                }
            }
        }
    }, [user, devDismissed, hasAutoOpened, setIsOnboardingModalOpen]);

    // 2. Fetch config when modal opens (either auto or manual)
    useEffect(() => {
        if (isOnboardingModalOpen && !configData) {
            const fetchConfig = async () => {
                try {
                    const response = await fetchWithAuth('/api/config/tasks');
                    if (response.ok) {
                        const data = await response.json();
                        setConfigData(data);
                    }
                } catch (err) {
                    console.error("Failed to load onboarding config:", err);
                }
            };
            fetchConfig();
        }
    }, [isOnboardingModalOpen, configData, fetchWithAuth]);

    // Blur the background when this opens
    useEffect(() => {
        if (isOnboardingModalOpen) {
            setIsPageBlurred(true);
        } else {
            setIsPageBlurred(false);
        }
        return () => setIsPageBlurred(false); 
    }, [isOnboardingModalOpen, setIsPageBlurred]);

    // Filter out meta tasks (like Vetra Score)
    const standardTasks = useMemo(() => {
        if (!configData) return [];
        return Object.entries(configData.tasks).filter(([_, config]) => !config.is_meta_task);
    }, [configData]);

    // Always reset the wizard to an empty state when it opens 
    // so the user is forced to make a fresh choice.
    useEffect(() => {
        if (isOnboardingModalOpen) {
            setSelectedIndustry('');
            setSelectedTasks([]);
            setSaveError(null);
        }
    }, [isOnboardingModalOpen]);

    // Handle Industry Selection
    const handleIndustryChange = (e) => {
        const industryKey = e.target.value;
        setSelectedIndustry(industryKey);

        // Auto-select the recommended tasks for this industry
        if (configData && configData.presets[industryKey]) {
            const recommended = Object.keys(configData.presets[industryKey].recommended_tasks || {});
            setSelectedTasks(recommended);
        }
    };

    // Handle Checkbox Toggles
    const handleTaskToggle = (taskKey) => {
        setSelectedTasks(prev => 
            prev.includes(taskKey) 
                ? prev.filter(t => t !== taskKey) 
                : [...prev, taskKey]
        );
    };

    // Save to Database
    const handleSave = async () => {
        setIsSaving(true);
        setSaveError(null);
        
        const currentPrefs = user?.analysis_preferences || {};
        const updatedPrefs = {
            ...currentPrefs,
            industry: selectedIndustry,
            displayed_tasks: selectedTasks.length > 0 ? selectedTasks : currentPrefs.displayed_tasks,
            default_tasks: selectedTasks.length > 0 ? selectedTasks : currentPrefs.default_tasks
        };

        const result = await updateUserSetting('analysis_preferences', updatedPrefs);

        if (result.success) {
            if (process.env.NODE_ENV === 'development') setDevDismissed(true);
            setIsOnboardingModalOpen(false); // Close the modal!
        } else {
            console.error("Failed to save onboarding preferences", result.error);
            setSaveError(result.error || "Failed to save preferences. Please try again.");
        }
        setIsSaving(false);
    };

    const handleClose = () => {
        if (process.env.NODE_ENV === 'development') setDevDismissed(true);
        setIsOnboardingModalOpen(false);
    };

    const getTaskDisplayName = (taskKey, yamlFallback) => {
        const camelCaseKey = taskKey.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        const transKey = `selector.analysisTasks.${camelCaseKey}`;
        const translated = t(transKey);
        return translated !== transKey ? translated : yamlFallback;
    };

    if (!isOnboardingModalOpen || !configData) return null;

    const presets = configData.presets || {};
    const activePreset = presets[selectedIndustry];

    return ReactDOM.createPortal(
        <div className="onboarding-overlay">
            <div className="onboarding-content">
                
                {/* --- HEADER WITH NEW CLOSE BUTTON --- */}
                <div className="onboarding-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            {t('onboardingModal.titleConfig', 'Vetra AI Analysis Configuration')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                            {t('onboardingModal.subtitle')}
                        </Typography>
                    </div>
                    <IconButton 
                        onClick={handleClose}
                        aria-label="Close configuration modal"
                        sx={{ 
                            color: 'rgba(255,255,255,0.4)', 
                            mt: -1, 
                            mr: -1,
                            '&:hover': { color: 'rgba(255,255,255,0.9)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>

                {/* BODY (SCROLLABLE) */}
                <div className="onboarding-body">
                    {saveError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>
                    )}

                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                        <InputLabel id="industry-label" sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-focused': { color: 'var(--secondary-color)' } }}>
                            {t('onboardingModal.industryLabel')}
                        </InputLabel>
                        <Select
                            labelId="industry-label"
                            value={selectedIndustry}
                            onChange={handleIndustryChange}
                            label={t('onboardingModal.industryLabel')}
                            sx={{ 
                                color: 'white', 
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' }
                            }}
                            MenuProps={{
                                style: { zIndex: 1500 }, 
                                PaperProps: { sx: { bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' } }
                            }}
                            // --- RENDER ICONS WHEN CLOSED ---
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {industryIcons[selected] || <BusinessIcon fontSize="small" sx={{ color: 'var(--secondary-color)' }} />}
                                    {t(`onboardingModal.industries.${selected}.name`, presets[selected]?.display_name)}
                                </Box>
                            )}
                        >
                            {Object.entries(presets).map(([key, preset]) => (
                                <MenuItem key={key} value={key}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {industryIcons[key] || <BusinessIcon fontSize="small" sx={{ color: '#171F29' }} />}
                                        {t(`onboardingModal.industries.${key}.name`, preset.display_name)}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* TASK LIST CONTAINER */}
                    {activePreset && (
                        <div className="task-list-container">
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--secondary-color)' }}>
                                    {t('onboardingModal.recommendedTitle')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {t(`onboardingModal.industries.${selectedIndustry}.description`, activePreset.description)}
                                </Typography>
                            </Box>

                            {standardTasks.map(([taskKey, taskData]) => {
                                const isRecommended = activePreset.recommended_tasks?.[taskKey] !== undefined;
                                const displayName = getTaskDisplayName(taskKey, taskData.display_name);
                                const tooltipDescription = t(`verificationPage.scoreDefinitions.${taskKey}`);
                                
                                let explanationText = t(`onboardingModal.fallbackDescriptions.${taskKey}`, taskData.description);
                                if (isRecommended && activePreset.recommended_tasks[taskKey]) {
                                    explanationText = t(`onboardingModal.industries.${selectedIndustry}.reasons.${taskKey}`, activePreset.recommended_tasks[taskKey]);
                                }

                                return (
                                    <div key={taskKey} className={`task-item-row ${isRecommended ? 'recommended' : ''}`}>
                                        <Checkbox 
                                            checked={selectedTasks.includes(taskKey)}
                                            onChange={() => handleTaskToggle(taskKey)}
                                            sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: 'var(--secondary-color)' }, mt: -0.25 }}
                                        />
                                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                                    {displayName}
                                                </Typography>
                                                {isRecommended && (
                                                    <Typography sx={{ fontSize: '0.7rem', bgcolor: 'var(--secondary-color)', color: 'white', px: 1, borderRadius: 1, fontWeight: 'bold' }}>
                                                        {t('onboardingModal.recommendedBadge')}
                                                    </Typography>
                                                )}
                                                
                                                <Tooltip 
                                                    title={tooltipDescription} 
                                                    arrow 
                                                    placement="top"
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', ml: 0.5 }} />
                                                </Tooltip>
                                            </Box>
                                            
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5, pr: 2 }}>
                                                {explanationText}
                                            </Typography>
                                        </Box>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* FOOTER */}
                <div className="onboarding-footer">
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {t('onboardingModal.editLaterNote')}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {process.env.NODE_ENV === 'development' && (
                            <Button 
                                onClick={handleClose} 
                                sx={{ color: 'rgba(255,255,255,0.4)' }}
                            >
                                {t('onboardingModal.skipButton')}
                            </Button>
                        )}
                        
                        <Tooltip 
                            title={(!selectedIndustry || selectedTasks.length === 0) ? t('onboardingModal.saveDisabledTooltip') : ''}
                            arrow
                            placement="top"
                            PopperProps={{ className: 'global-dark-tooltip' }}
                        >
                            <span style={{ display: 'inline-block', cursor: (!selectedIndustry || selectedTasks.length === 0) ? 'not-allowed' : 'pointer' }}>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSave} 
                                    disabled={!selectedIndustry || selectedTasks.length === 0 || isSaving}
                                    className="onboarding-action-button"
                                    style={{ pointerEvents: (!selectedIndustry || selectedTasks.length === 0) ? 'none' : 'auto' }}
                                >
                                    {isSaving ? <CircularProgress size={24} color="inherit" /> : t('onboardingModal.saveButton')}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </div>

            </div>
        </div>,
        document.body
    );
}

export default OnboardingModal;